import axiosInstance from './axios';

/**
 * Earnings service for fetching track earnings data
 */
class EarningsService {
  /**
   * Get earnings summary for a single track
   * @param {string} isrc - Track ISRC
   * @returns {Promise<Object|null>} Earnings data or null
   */
  static async getTrackEarnings(isrc) {
    try {
      const response = await axiosInstance.get(
        `/spotontrack/stream_count/${isrc}/earnings?platform=spotify`
      );
      
      if (response?.data?.earnings?.summary) {
        return response.data.earnings.summary;
      }
      return null;
    } catch (error) {
      console.warn(`Failed to fetch earnings for ISRC ${isrc}:`, error.message);
      return null;
    }
  }

  /**
   * Get earnings data for multiple tracks
   * @param {Array<string>} isrcList - Array of track ISRCs
   * @returns {Promise<Map>} Map of ISRC to earnings data
   */
  static async getMultipleTrackEarnings(isrcList) {
    if (!isrcList || isrcList.length === 0) {
      return new Map();
    }

    // Fetch earnings for all tracks in parallel
    const earningsPromises = isrcList.map(async (isrc) => {
      const earnings = await this.getTrackEarnings(isrc);
      return { isrc, earnings };
    });

    try {
      const results = await Promise.all(earningsPromises);
      
      // Create a Map for easy lookup
      const earningsMap = new Map();
      results.forEach(({ isrc, earnings }) => {
        earningsMap.set(isrc, earnings);
      });

      return earningsMap;
    } catch (error) {
      console.error('Error fetching multiple track earnings:', error);
      return new Map();
    }
  }

  /**
   * Calculate projected earnings for user's shares
   * @param {number} projectedMonthlyEarnings - Track's total projected monthly earnings
   * @param {number} userShares - Number of shares user owns
   * @param {number} totalShares - Total shares available for the track
   * @returns {number} User's projected earnings
   */
  static calculateUserProjectedEarnings(projectedMonthlyEarnings, userShares, totalShares) {
    if (!projectedMonthlyEarnings || !userShares || !totalShares || totalShares === 0) {
      return 0;
    }

    const sharePercentage = userShares / totalShares;
    return projectedMonthlyEarnings * sharePercentage;
  }

  /**
   * Format earnings value for display
   * @param {number} earnings - Earnings amount
   * @returns {string} Formatted earnings string
   */
  static formatEarnings(earnings) {
    if (!earnings || earnings === 0) {
      return '$0.00';
    }

    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(earnings);
  }

  /**
   * Determine color for projected earnings based on trend
   * @param {number} projectedEarnings - User's projected monthly earnings
   * @param {Object} earningsData - Full earnings data from API
   * @param {number} userShares - Number of shares user owns
   * @param {number} totalShares - Total shares for the track
   * @returns {string} Color value for display
   */
  static getProjectedEarningsColor(projectedEarnings, earningsData, userShares, totalShares) {
    if (!projectedEarnings || !earningsData || projectedEarnings === 0) {
      return 'gray.500'; // No data or zero earnings
    }

    // Calculate user's portion of current month earnings for comparison
    const userCurrentEarnings = this.calculateUserProjectedEarnings(
      earningsData.current_month_earnings,
      userShares,
      totalShares
    );

    // Calculate percentage difference
    const percentageDiff = userCurrentEarnings > 0 
      ? ((projectedEarnings - userCurrentEarnings) / userCurrentEarnings) * 100 
      : (projectedEarnings > 0 ? 100 : 0);

    // Determine color based on trend
    if (percentageDiff > 5) {
      return 'green.600'; // Trending up significantly (>5%)
    } else if (percentageDiff < -5) {
      return 'red.600';   // Trending down significantly (<-5%)
    } else {
      return 'white';     // Neutral/stable (within ±5%)
    }
  }
}

export default EarningsService;
